import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment as env } from '@env/environment';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { switchMap, shareReplay, tap, catchError } from 'rxjs/operators';

import { OAuthToken } from '@shared/models';
import { User } from '@shared/models/user.model';

@Injectable({
	providedIn: 'root'
})
export class KeycloakService {
	// Using signals for more reactive state management
	private readonly httpClient = inject(HttpClient);
	private readonly router = inject(Router);

	// Using private class field with type annotation
	#currentUserInfo$?: Observable<User>;
	currentUser = signal<User | null>(null);

	// Using signal-like reactive state
	isLoggedIn$ = new BehaviorSubject<boolean>(this.#hasToken());

	login(code: string, page: string): Observable<boolean> {
		const headers = new HttpHeaders().append(
			'Content-Type',
			'application/x-www-form-urlencoded;charset=utf-8'
		);

		let params = new HttpParams();
		params = params.append('grant_type', 'authorization_code');
		params = params.append('client_id', 'web');
		params = params.append('code', code);
		params = params.append('redirect_uri', `${env.appUrl}/?page=${page}`);

		return this.httpClient
			.post<OAuthToken>(`${env.authUrl}/realms/hris/protocol/openid-connect/token`, params, {
				headers
			})
			.pipe(
				switchMap((oauth: OAuthToken) => {
					this.#saveAccessToken(oauth.access_token);
					this.#saveRefreshToken(oauth.refresh_token);
					this.#saveIdToken(oauth.id_token);
					this.isLoggedIn$.next(true);

					return of(true);
				}),
				catchError(() => of(false))
			);
	}

	refreshToken(refreshToken: string): Observable<OAuthToken> {
		const headers = new HttpHeaders().append(
			'Content-Type',
			'application/x-www-form-urlencoded;charset=utf-8'
		);

		let params = new HttpParams();
		params = params.append('grant_type', 'refresh_token');
		params = params.append('client_id', 'web');
		params = params.append('refresh_token', refreshToken);

		return this.httpClient
			.post<OAuthToken>(`${env.authUrl}/realms/hris/protocol/openid-connect/token`, params, {
				headers
			})
			.pipe(
				tap((oauth: OAuthToken) => {
					this.#saveAccessToken(oauth.access_token);
					this.#saveRefreshToken(oauth.refresh_token);
					this.#saveIdToken(oauth.id_token);
				})
			);
	}

	getUser(): Observable<User> {
		if (!this.#currentUserInfo$) {
			this.#currentUserInfo$ = this.httpClient
				.get<User>('/api/v1/records/me')
				.pipe(shareReplay({ bufferSize: 1, refCount: true }));
		}
		return this.#currentUserInfo$.pipe(tap((user) => this.currentUser.set(user)));
	}

	// Private methods for token management with # private method syntax
	#saveAccessToken(token: string): void {
		localStorage.setItem('access_token', token);
	}

	#saveRefreshToken(token: string): void {
		localStorage.setItem('refresh_token', token);
	}

	#saveIdToken(token: string): void {
		localStorage.setItem('id_token', token);
	}

	removeToken(): void {
		localStorage.removeItem('access_token');
	}

	getAccessToken(): string | null {
		const token = localStorage.getItem('access_token');
		return token;
	}

	getRefreshToken(): string | null {
		return localStorage.getItem('refresh_token');
	}

	getIdToken(): string | null {
		return localStorage.getItem('id_token');
	}

	clearToken(): void {
		localStorage.clear();
	}

	#hasToken(): boolean {
		return !!this.getAccessToken();
	}

	logout(): void {
		const url = `${env.authUrl}/realms/hris/protocol/openid-connect/logout?id_token_hint=${this.getIdToken()}&post_logout_redirect_uri=${env.appUrl}&client_id=web`;
		this.clearToken();
		location.href = url;
	}

}
